/* RESPONSIBLE TEAM: team-inbox */
import { generateSafeEmoji } from 'embercom/lib/emoji';
import { VISIBILITY_FILTER_TYPE } from './filter-types';
import { inject as service } from '@ember/service';
import { setOwner } from '@ember/application';

// eslint-disable-next-line @intercom/intercom/no-component-inheritance
export class TeamVisibilityFilter {
  @service intl;
  #allTeammatesValue = 'everyone';
  #myselfValue = 'myself';
  #getTeamFilterPaywall;

  constructor(appService, owner, getTeamFilterPaywall) {
    setOwner(this, owner);
    this.visibilityFilterType = VISIBILITY_FILTER_TYPE.TEAM;
    this.appService = appService;
    this.#getTeamFilterPaywall = getTeamFilterPaywall;
  }

  get app() {
    return this.appService.app;
  }

  get sortedTeams() {
    return this.app.teams.sortBy('name');
  }

  get teamFilterPaywall() {
    return this.#getTeamFilterPaywall?.();
  }

  get canSetMacroVisibility() {
    return this.appService.app.canSetMacroVisibility;
  }

  filterSavedReplies(savedReplies, visibility) {
    if (visibility === this.#allTeammatesValue) {
      return savedReplies.filterBy('visibility', this.#allTeammatesValue);
    } else {
      return savedReplies.filter(({ visibleToTeamIds }) => {
        if (visibleToTeamIds) {
          visibleToTeamIds = visibleToTeamIds.map((id) => {
            return parseInt(id, 10);
          });
          return visibleToTeamIds.includes(parseInt(visibility, 10));
        }

        return false;
      });
    }
  }

  filterLabel(visibility) {
    if (visibility === this.#allTeammatesValue) {
      return this.intl.t('inbox.manage-macros.filter.teammates.all');
    } else if (visibility === this.#myselfValue) {
      return this.intl.t('inbox.manage-macros.filter.teammates.myself');
    } else {
      let team = this.sortedTeams.findBy('id', visibility);
      return this.intl.t('inbox.manage-macros.filter.teammates.specific', {
        emoji: team.avatar_emoji,
        teamName: team.name,
      });
    }
  }

  _createPaywalledItem(baseItem, teamId) {
    return {
      ...baseItem,
      component: 'paywalls/select-item',
      componentShouldReplaceItem: false,
      paywallOpenUpgradeModal: () => this.teamFilterPaywall.openUpgradeModal(),
      paywallAnalyticsEventData: {
        ...this.teamFilterPaywall.analyticsEventData,
        context: 'macro_filter_dropdown',
        place: 'macros_list',
        filterType: 'team',
        teamId,
      },
    };
  }

  items() {
    let everyoneItem = {
      text: this.intl.t('inbox.manage-macros.filter.teammates.all-teammates'),
      value: { type: this.visibilityFilterType, value: this.#allTeammatesValue },
      icon: 'multiple-people',
    };

    let myselfItem = {
      text: this.intl.t('inbox.manage-macros.filter.teammates.myself'),
      value: { type: this.visibilityFilterType, value: this.#myselfValue },
      icon: 'person',
    };

    let teamItems = this.sortedTeams.map(({ name, id, avatar_emoji }) => {
      let baseItem = {
        text: name,
        value: { type: this.visibilityFilterType, value: id },
        component: 'settings/saved-replies/team-select-item',
        componentAttrs: { teamAvatar: generateSafeEmoji(avatar_emoji) },
      };

      if (!this.canSetMacroVisibility && this.teamFilterPaywall?.isActive) {
        return this._createPaywalledItem(baseItem, id);
      }

      return baseItem;
    });

    return [
      {
        heading: this.intl.t('inbox.manage-macros.filter.teammates.heading'),
        items: this.appService.app.canUseMacrosSettingsWithFiltersAndPagination
          ? [everyoneItem, myselfItem, ...teamItems]
          : [everyoneItem, myselfItem, ...teamItems],
      },
    ];
  }
}
